import * as actionTypes from './actionTypes';
import axios from '../../global/axios';

const MIN_LOADING_MS = 5000;

// Fetch documents list
export const fetchDocuments = () => {
    return dispatch => {
        dispatch({ type: actionTypes.FETCH_DOCUMENTS_START });
        axios.get('/slides/editor.json')
            .then(res => {
                dispatch({ type: actionTypes.FETCH_DOCUMENTS_SUCCESS, documents: res.data });
            })
            .catch(err => {
                dispatch({ type: actionTypes.FETCH_DOCUMENTS_FAILURE, error: err.message });
            });
    };
};

// Fetch single document
export const fetchDocument = (id) => {
    return dispatch => {
        dispatch({ type: actionTypes.FETCH_DOCUMENT_START });
        axios.get('/slides/editor/' + id + '.json')
            .then(res => {
                dispatch({ type: actionTypes.FETCH_DOCUMENT_SUCCESS, document: res.data });
            })
            .catch(err => {
                dispatch({ type: actionTypes.FETCH_DOCUMENT_FAILURE, error: err.message });
            });
    };
};

// Create blank document
export const createDocument = (name, callback) => {
    return dispatch => {
        dispatch({ type: actionTypes.CREATE_DOCUMENT_START });
        axios.post('/slides/editor.json', { name: name || 'Untitled Document' }, {
            headers: { 'Content-Type': 'application/json' }
        })
            .then(res => {
                dispatch({ type: actionTypes.CREATE_DOCUMENT_SUCCESS, document: res.data });
                if (callback) callback(res.data);
            })
            .catch(err => {
                dispatch({ type: actionTypes.CREATE_DOCUMENT_FAILURE, error: err.message });
            });
    };
};

// Import document from file
export const importDocument = (file, addBlankSlides, callback) => {
    return dispatch => {
        dispatch({ type: actionTypes.IMPORT_DOCUMENT_START });
        const formData = new FormData();
        formData.append('file', file);
        formData.append('add_blank_slides', addBlankSlides);
        Promise.all([
            axios.post('/slides/editor/import', formData),
            new Promise(resolve => setTimeout(resolve, MIN_LOADING_MS)),
        ])
            .then(([res]) => {
                dispatch({ type: actionTypes.IMPORT_DOCUMENT_SUCCESS, document: res.data });
                if (callback) callback(res.data);
            })
            .catch(err => {
                let message = 'Import failed';
                if (err.response && err.response.data && err.response.data.error) {
                    message = err.response.data.error;
                }
                dispatch({ type: actionTypes.IMPORT_DOCUMENT_FAILURE, error: message });
            });
    };
};

// AI import document from file
export const aiImportDocument = (file, customPrompt, addBlankSlides, callback) => {
    return dispatch => {
        dispatch({ type: actionTypes.IMPORT_DOCUMENT_START });
        const formData = new FormData();
        formData.append('file', file);
        formData.append('add_blank_slides', addBlankSlides);
        if (customPrompt) formData.append('prompt', customPrompt);
        Promise.all([
            axios.post('/slides/editor/ai_import', formData),
            new Promise(resolve => setTimeout(resolve, MIN_LOADING_MS)),
        ])
            .then(([res]) => {
                dispatch({ type: actionTypes.IMPORT_DOCUMENT_SUCCESS, document: res.data });
                if (callback) callback(res.data);
            })
            .catch(err => {
                let message = 'AI import failed';
                if (err.response && err.response.data && err.response.data.error) {
                    message = err.response.data.error;
                }
                dispatch({ type: actionTypes.IMPORT_DOCUMENT_FAILURE, error: message });
            });
    };
};

// Save document
export const saveDocument = (id, presentationJson, name) => {
    return dispatch => {
        dispatch({ type: actionTypes.UPDATE_DOCUMENT_START });
        const data = {};
        if (presentationJson) data.presentation_json = presentationJson;
        if (name) data.name = name;
        return axios.put('/slides/editor/' + id + '.json', data, {
            headers: { 'Content-Type': 'application/json' }
        })
            .then(res => {
                dispatch({ type: actionTypes.UPDATE_DOCUMENT_SUCCESS, document: res.data });
            })
            .catch(err => {
                dispatch({ type: actionTypes.UPDATE_DOCUMENT_FAILURE, error: err.message });
            });
    };
};

// Delete document
export const deleteDocument = (id, callback) => {
    return dispatch => {
        dispatch({ type: actionTypes.DELETE_DOCUMENT_START });
        axios.delete('/slides/editor/' + id + '.json')
            .then(() => {
                dispatch({ type: actionTypes.DELETE_DOCUMENT_SUCCESS, id: id });
                if (callback) callback();
            })
            .catch(err => {
                dispatch({ type: actionTypes.DELETE_DOCUMENT_FAILURE, error: err.message });
            });
    };
};

// Local state actions
export const selectSlide = (index) => {
    return { type: actionTypes.SELECT_SLIDE, index: index };
};

export const selectElement = (index) => {
    return { type: actionTypes.SELECT_ELEMENT, index: index };
};

export const updatePresentationLocal = (presentation) => {
    return { type: actionTypes.UPDATE_PRESENTATION_LOCAL, presentation: presentation };
};

// AI Draft
export const aiDraftDocument = (file, customPrompt, addBlankSlides, callback) => {
    return dispatch => {
        dispatch({ type: actionTypes.IMPORT_DOCUMENT_START });
        const formData = new FormData();
        formData.append('file', file);
        formData.append('add_blank_slides', addBlankSlides);
        if (customPrompt) formData.append('prompt', customPrompt);
        Promise.all([
            axios.post('/slides/editor/ai_draft', formData),
            new Promise(resolve => setTimeout(resolve, MIN_LOADING_MS)),
        ])
            .then(([res]) => {
                dispatch({ type: actionTypes.AI_DRAFT_SUCCESS, draft: res.data });
                if (callback) callback(res.data);
            })
            .catch(err => {
                let message = 'AI draft failed';
                if (err.response && err.response.data && err.response.data.error) {
                    message = err.response.data.error;
                }
                dispatch({ type: actionTypes.IMPORT_DOCUMENT_FAILURE, error: message });
            });
    };
};

export const generateFromDraft = (name, slides, images, addBlankSlides, callback) => {
    return dispatch => {
        dispatch({ type: actionTypes.GENERATE_FROM_DRAFT_START });
        axios.post('/slides/editor/generate_from_draft', {
            draft: JSON.stringify({ name, slides }),
            images: JSON.stringify(images),
            add_blank_slides: addBlankSlides,
        })
            .then(res => {
                dispatch({ type: actionTypes.IMPORT_DOCUMENT_SUCCESS, document: res.data });
                if (callback) callback(res.data);
            })
            .catch(err => {
                let message = 'Generation failed';
                if (err.response && err.response.data && err.response.data.error) {
                    message = err.response.data.error;
                }
                dispatch({ type: actionTypes.GENERATE_FROM_DRAFT_FAILURE, error: message });
            });
    };
};

export const updateDraftSlides = (slides) => ({
    type: actionTypes.UPDATE_DRAFT_SLIDES,
    slides
});

export const clearDraft = () => ({
    type: actionTypes.CLEAR_DRAFT,
});
